import { useEffect } from 'react';

import { useCheckoutContext, Article, CartMessages } from '@zooplus/checkout-state';
import { TRACKING_PAGE } from '@zooplus/tracker-listener';

import CartEmpty from '@components/cart/cart-empty';
import CartOverview from '@components/cart/cart-overview';
import { EmptyCartSkeleton } from '@components/common/checkout-skeleton';
import GeneralErrorMessage from '@components/common/notifications/general-error-message';
import PageLoader from '@components/common/page-loader';
import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import useExperimentsTracking from '@hooks/use-adobe-tracking/use-experiments-tracking';
import useInteractionTracking from '@hooks/use-adobe-tracking/use-interaction-tracking';
import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import useDeliveryServicesMetrics from '@hooks/use-delivery-services-metrics';
import { logError } from '@services/sentry-service';
import { CHECKOUT_STATE_PROJECT_ID, PAGE_TYPE } from '@utilities/constants';
import { getPageType, goToCart, isArrayNotEmpty, isObjectNotNullOrEmpty } from '@utilities/utils';

const Cart = () => {
  const {
    checkoutState,
    isFetching,
    mainTogglesMap = {},
    pageType,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { trackCartView, trackCartViewUsingTrackerListener } = useCartTracking();
  const { trackPageViewUsingTrackerListener } = usePageTracking();
  const { trackExperimentsUsingTrackerListener } = useExperimentsTracking();
  const { trackInteractionsUsingTrackerListener } = useInteractionTracking();
  const cartMessages = checkoutState?.cart.cartMessages;
  const hasCartMessages: boolean = isArrayNotEmpty(cartMessages as CartMessages[]);
  const mapCartMessagesToTrackingComponents = (messages: CartMessages[]) =>
    messages.map((message) => ({
      name: message.type,
      id: message.code,
    }));

  useDeliveryServicesMetrics();

  const articles = checkoutState?.cart?.articles;
  const isNewOverviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_OVERVIEW_PAGE;
  const sid = checkoutState?.cart.sid;

  useEffect(() => {
    goToCart(!!isFetching, isNewOverviewPage, isArrayNotEmpty(articles as Article[]));
  }, [articles, isFetching, isNewOverviewPage]);

  useEffect(() => {
    if (sid) {
      trackPageViewUsingTrackerListener(TRACKING_PAGE.CHECKOUT_CART);
      trackExperimentsUsingTrackerListener(TRACKING_PAGE.CHECKOUT_CART);
    }
  }, [sid]);

  useEffect(() => {
    if (hasCartMessages && cartMessages) {
      const components = mapCartMessagesToTrackingComponents(cartMessages);
      trackInteractionsUsingTrackerListener(getPageType(), components);
    }
  }, [cartMessages]);

  useEffect(() => {
    if (checkoutState?.cart && !isFetching) {
      (async () => {
        try {
          await trackCartViewUsingTrackerListener();
        } catch (err) {
          logError(err, {
            extra: {
              message: 'Failed to track cart view via tracker listener',
            },
          });
        }
      })();
    }
  }, [checkoutState?.cart, isFetching]);

  useEffect(() => {
    if (articles !== undefined) {
      trackCartView();
    }
  }, [articles, trackCartView]);

  useEffect(() => {
    const isWebEmptyCart = pageType === PAGE_TYPE.WEB_EMPTY_CART;
    const hasOneArticle = articles?.length === 1;

    if (isWebEmptyCart && hasOneArticle) {
      window.scrollTo(0, 0);
    }
  }, [pageType, articles?.length]);

  const isCartObjectEmpty = !isObjectNotNullOrEmpty(checkoutState?.cart);
  const shouldShowOverview = isNewOverviewPage && isArrayNotEmpty(articles as Article[]);

  if (isFetching) {
    return (
      <>
        <PageLoader />
        <EmptyCartSkeleton />
      </>
    );
  }

  if (isCartObjectEmpty) {
    return <GeneralErrorMessage />;
  }

  if (shouldShowOverview) {
    return <CartOverview />;
  }

  return <CartEmpty />;
};

export default Cart;
